import React, { useState, useEffect, useRef } from 'react';

const syne  = { fontFamily: "'Syne', 'Inter', sans-serif" };
const inter = { fontFamily: "'Inter', sans-serif" };

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const ease  = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// ─── small SVG glyphs ───────────────────────────────────────────────────────
const Spark = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2 L13.8 8.2 L20 10 L13.8 11.8 L12 18 L10.2 11.8 L4 10 L10.2 8.2 Z" />
    <path d="M19 14 L19.7 16.3 L22 17 L19.7 17.7 L19 20 L18.3 17.7 L16 17 L18.3 16.3 Z" opacity=".75" />
  </svg>
);
const Check = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Lock = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

// ─── card label pill ────────────────────────────────────────────────────────
function Label({ color, children }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      fontSize: 11, fontWeight: 700,
      letterSpacing: "0.16em", textTransform: "uppercase",
      padding: "6px 12px 5px",
      borderRadius: 999,
      background: `${color}1c`,
      color,
      border: `1px solid ${color}44`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color, boxShadow: `0 0 8px ${color}` }} />
      {children}
    </span>
  );
}

// ═══ Card 1 mockup — AI Chat ═════════════════════════════════════════════════
function ChatMockup({ accent }) {
  return (
    <div style={{
      width: "100%", maxWidth: 520,
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 22, padding: 22,
      boxShadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.4)",
      backdropFilter: "blur(16px)",
    }}>
      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        paddingBottom: 14, marginBottom: 16,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 999,
            background: "linear-gradient(135deg, #4a3c2a, #2a1f15)",
            border: "2px solid rgba(255,255,255,0.1)",
          }} />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14, fontWeight: 700 }}>
              <span>sarachad</span>
              <span style={{ color: accent }}><Check /></span>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: "#34C759" }} />
              AI auto-reply active
            </div>
          </div>
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 9px",
          background: `${accent}1c`, border: `1px solid ${accent}55`,
          borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: accent,
        }}>
          <Spark size={10} color={accent} />
          Live
        </div>
      </div>

      {/* fan */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", maxWidth: "78%", marginBottom: 12 }}>
        <div style={{ width: 26, height: 26, borderRadius: 999, background: "linear-gradient(135deg, #4a2418, #2a140c)", flex: "none" }} />
        <div style={{
          padding: "9px 13px", background: "#16161c",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: "16px 16px 16px 4px",
          fontSize: 13.5, lineHeight: 1.45, color: "rgba(255,255,255,0.92)",
        }}>
          Love the quick high‑protein recipes you share 😋
        </div>
      </div>

      {/* AI */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, maxWidth: "82%" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flex: "none", paddingTop: 4 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: `linear-gradient(135deg, ${accent}, ${accent}aa)`,
              display: "grid", placeItems: "center",
              boxShadow: `0 4px 10px ${accent}55`,
            }}><Spark size={12} color="#fff" /></div>
            <div style={{ fontSize: 9, fontWeight: 700, color: accent, letterSpacing: "0.04em" }}>Premsi AI</div>
          </div>
          <div style={{
            padding: "9px 13px",
            background: `linear-gradient(180deg, ${accent}24, ${accent}10)`,
            border: `1px solid ${accent}55`,
            borderRadius: "16px 16px 4px 16px",
            fontSize: 13.5, lineHeight: 1.45, color: "#fff",
          }}>
            Nicee, I actually have an exclusive <i>Fast Recipes</i> bundle — weekly meals, shopping lists, simple instructions 😊
          </div>
        </div>
      </div>

      {/* locked offer */}
      <div style={{
        marginLeft: "auto", width: "min(82%, 280px)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 14, overflow: "hidden", marginBottom: 14,
      }}>
        <div style={{
          height: 64,
          background: `radial-gradient(120% 100% at 30% 20%, ${accent}33, transparent 60%), linear-gradient(135deg, #2b1d4d, #1a1326)`,
          display: "grid", placeItems: "center",
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 999,
            border: "1.5px solid rgba(255,255,255,0.7)",
            display: "grid", placeItems: "center",
          }}><Lock size={13} /></div>
        </div>
        <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>Fast & Healthy Recipes ❤️</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>$5 · sent by AI</div>
          </div>
          <div style={{
            padding: "5px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
            background: `linear-gradient(180deg, ${accent}, ${accent}cc)`,
            boxShadow: `0 4px 12px ${accent}55`,
          }}>Unlock</div>
        </div>
      </div>

      {/* composer */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 10px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 999,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: 999,
          background: `${accent}28`, color: accent,
          display: "grid", placeItems: "center",
          border: `1px solid ${accent}55`,
        }}><Spark size={11} color={accent} /></div>
        <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.45)", flex: 1 }}>
          Premsi AI is typing<span style={{ letterSpacing: 2, marginLeft: 2 }}>···</span>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.55)",
          padding: "3px 9px", borderRadius: 999,
          background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)",
        }}>0.8s</div>
      </div>
    </div>
  );
}

// ═══ Card 2 mockup — AI Personality / Settings ══════════════════════════════
function SettingsMockup({ accent }) {
  const modes = ["Friendly",  "Pro", "Hype"];
  return (
    <div style={{
      width: "100%", maxWidth: 420,
      background: "rgba(14,14,18,0.85)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 22, overflow: "hidden",
      boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
    }}>
      {/* hero strip */}
      <div style={{ padding: "18px 20px 20px", background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 13,
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.25)",
            display: "grid", placeItems: "center",
          }}><Spark size={22} color="#fff" /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Premsi AI</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.9)", marginTop: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: "#34C759" }} />
              Active · auto-replying
            </div>
          </div>
          <div style={{ width: 42, height: 26, borderRadius: 999, background: "#34C759", position: "relative" }}>
            <div style={{ position: "absolute", top: 2, left: 18, width: 22, height: 22, borderRadius: 999, background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
        </div>
        <div style={{
          marginTop: 14, padding: "12px 4px",
          background: "rgba(255,255,255,0.13)", border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
        }}>
          {[["142", "Replies today"], ["94%", "Happy fans"], ["$38", "Earned today"]].map(([n, l], i) => (
            <div key={l} style={{ textAlign: "center", borderLeft: i ? "1px solid rgba(255,255,255,0.2)" : "none" }}>
              <div style={{ ...syne, fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.78)", marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* body */}
      <div style={{ padding: "18px 20px 20px" }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
          color: "rgba(255,255,255,0.45)", marginBottom: 12,
        }}>Personality</div>

        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 9 }}>AI Mode</div>
        <div style={{
          display: "flex", padding: 3, gap: 2,
          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 10, marginBottom: 16,
        }}>
          {modes.map((m, i) => (
            <div key={m} style={{
              flex: 1, textAlign: "center", padding: "7px 0",
              fontSize: 12.5, fontWeight: 700, borderRadius: 8,
              background: i === 0 ? accent : "transparent",
              color: i === 0 ? "#fff" : "rgba(255,255,255,0.55)",
              boxShadow: i === 0 ? `0 4px 12px ${accent}55` : "none",
            }}>{m}</div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 600, marginBottom: 9 }}>
          <span>Warmth</span><span style={{ color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>Warm</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.08)", position: "relative", marginBottom: 18 }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "62%", borderRadius: 999, background: `linear-gradient(90deg, ${accent}88, ${accent})` }} />
          <div style={{ position: "absolute", left: "62%", top: "50%", transform: "translate(-50%,-50%)", width: 16, height: 16, borderRadius: 999, background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,0.4)" }} />
        </div>

        {[
          { l: "Use emojis in replies", on: true },
          { l: "Sell my products",      on: true },
          { l: "Hype mode after tips",  on: false },
        ].map((row, i, arr) => (
          <div key={row.l} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "11px 0", borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
          }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{row.l}</div>
            <div style={{
              width: 38, height: 22, borderRadius: 999,
              background: row.on ? accent : "rgba(255,255,255,0.12)",
              position: "relative",
            }}>
              <div style={{ position: "absolute", top: 2, left: row.on ? 18 : 2, width: 18, height: 18, borderRadius: 999, background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══ Card 3 mockup — Results / Earnings ══════════════════════════════════════
function ResultsMockup({ accent }) {
  const bars = [28, 42, 35, 58, 52, 70, 82, 94];
  const streams = [
    { label: "Paid DMs",        v: "$3,120", n: "127 unlocks" },
    { label: "PPV via AI",      v: "$2,840", n: "89 sent · 38 closed" },
    { label: "Tip menu nudges", v: "$1,650", n: "AI suggested" },
    { label: "Bundle upsells",  v: "$  850", n: "from chat" },
  ];
  return (
    <div style={{ width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 12 }}>
      {/* hero number */}
      <div style={{
        background: `linear-gradient(180deg, ${accent}1f, ${accent}08)`,
        border: `1px solid ${accent}3c`,
        borderRadius: 18, padding: "20px 22px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", right: -30, top: -30, width: 180, height: 180,
          background: `radial-gradient(circle, ${accent}33, transparent 65%)`, filter: "blur(20px)",
        }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: accent, letterSpacing: "0.12em", textTransform: "uppercase" }}>This month with AI</div>
            <div style={{ ...syne, fontSize: 42, fontWeight: 800, lineHeight: 1, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>$8,460</div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              marginTop: 8, padding: "3px 10px",
              background: `${accent}28`, border: `1px solid ${accent}55`,
              borderRadius: 999, fontSize: 11, fontWeight: 700, color: accent,
            }}>↑ 24% vs last month</div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
            {bars.map((h, i) => (
              <div key={i} style={{
                width: 8, height: `${h}%`, borderRadius: "2px 2px 0 0",
                background: i >= bars.length - 2 ? accent : `${accent}55`,
                border: `1px solid ${i >= bars.length - 2 ? accent : `${accent}66`}`,
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* streams list */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 16, overflow: "hidden",
      }}>
        {streams.map((s, i, arr) => (
          <div key={s.label} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px",
            borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: `${accent}22`, border: `1px solid ${accent}44`,
              display: "grid", placeItems: "center", color: accent,
            }}><Spark size={13} color={accent} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{s.n}</div>
            </div>
            <div style={{ ...syne, fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 14px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 12, fontSize: 12.5, color: "rgba(255,255,255,0.65)",
      }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: accent, boxShadow: `0 0 8px ${accent}` }} />
        AI closed <b style={{ color: "#fff", fontWeight: 700, margin: "0 4px" }}>3 sales</b> in the last hour. You haven't opened the app today.
      </div>
    </div>
  );
}

// ─── card data ────────────────────────────────────────────────────────────────
const CARDS = [
  {
    accent: "#3FB6E0",
    label: "AI · Direct Messages",
    headline: ["Chats with your fans,", "in your voice."],
    sub: "Premsi AI handles every DM 24/7 — recommends bundles, sends PPVs, closes tips. It sounds like you because it's trained on you.",
    bullets: ["Trained on your past chats", "Replies in under a second", "Works across all DMs"],
    bg: "#0a1421", glow: "#0a3a6a", Mockup: ChatMockup,
  },
  {
    accent: "#8B6BFF",
    label: "Personality",
    headline: ["Tuned to your", "brand and tone."],
    sub: "Pick a mode, set how warm it should be, decide on emojis. Your AI behaves the way you'd behave — and you can take over any time.",
    bullets: ["Friendly · Pro · Hype", "Adjustable warmth & emoji style", "Take over with one tap"],
    bg: "#0e0c1a", glow: "#3e1e8a", Mockup: SettingsMockup,
  },
  {
    accent: "#4DD7A5",
    label: "Results",
    headline: ["Sells while", "you sleep."],
    sub: "Replies in seconds, even at 3am. Closes sales while you're offline. Multiple income streams running in the background — you just check the totals.",
    bullets: ["+24% revenue on average", "Closes PPVs and bundles automatically", "Live dashboard with every sale"],
    bg: "#091610", glow: "#0a5a3e", Mockup: ResultsMockup,
  },
];

const cardBase = {
  position: "absolute", inset: 0, borderRadius: 32, overflow: "hidden",
  willChange: "transform, opacity",
  boxShadow: "0 60px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)",
};

// ─── progress dots ──────────────────────────────────────────────────────────
function Dots({ active, accent }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 10,
      position: "absolute", right: -28, top: "50%", transform: "translateY(-50%)",
    }}>
      {[0, 1, 2].map((i) => {
        const on = i === active;
        return (
          <div key={i} style={{
            width: 6, height: on ? 22 : 6, borderRadius: 999,
            background: on ? accent : "rgba(255,255,255,0.2)",
            boxShadow: on ? `0 0 12px ${accent}` : "none",
            transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
          }} />
        );
      })}
    </div>
  );
}

// ─── card body ──────────────────────────────────────────────────────────────
function Card({ data }) {
  const { accent, label, headline, sub, bullets, bg, glow, Mockup } = data;
  return (
    <div className="aiss-card" style={{
      width: "100%", height: "100%",
      background: bg,
      position: "relative", overflow: "hidden",
      display: "grid",
      gridTemplateColumns: "1.05fr 1fr",
      alignItems: "center",
      gap: 64,
      padding: "64px 72px",
    }}>
      {/* glow */}
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: glow, filter: "blur(140px)", opacity: 0.55,
        top: -200, right: -120, pointerEvents: "none",
      }} />
      {/* grain */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize: "3px 3px",
      }} />

      {/* left text */}
      <div className="aiss-card-text" style={{ position: "relative", zIndex: 1, maxWidth: 520 }}>
        <Label color={accent}>{label}</Label>
        <h2 style={{
          ...syne,
          margin: "20px 0 18px",
          fontSize: "clamp(40px, 4.6vw, 64px)",
          lineHeight: 1.02,
          letterSpacing: "-0.025em",
          fontWeight: 800,
          color: "#fff",
        }}>
          <span>{headline[0]}</span><br />
          <span style={{
            color: accent,
            background: `linear-gradient(180deg, ${accent}, ${accent}aa)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>{headline[1]}</span>
        </h2>
        <p style={{
          ...inter, margin: "0 0 26px",
          fontSize: 17, lineHeight: 1.55,
          color: "rgba(255,255,255,0.62)",
          textWrap: "pretty",
        }}>{sub}</p>

        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 11 }}>
          {bullets.map((b) => (
            <li key={b} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
              <span style={{
                width: 20, height: 20, borderRadius: 999, flex: "none",
                background: `${accent}28`, border: `1px solid ${accent}55`,
                color: accent, display: "grid", placeItems: "center",
              }}><Check size={11} /></span>
              {b}
            </li>
          ))}
        </ul>
      </div>

      {/* right mockup */}
      <div className="aiss-card-mock" style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "flex-end" }}>
        <Mockup accent={accent} />
      </div>
    </div>
  );
}

// ─── filler / spacer sections (above and below) ──────────────────────────────
function Spacer({ children, hint }) {
  return (
    <section className="aiss-spacer">
      {/* faint grid */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0,
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
        maskImage: "radial-gradient(70% 70% at 50% 50%, #000, transparent 80%)",
        WebkitMaskImage: "radial-gradient(70% 70% at 50% 50%, #000, transparent 80%)",
        pointerEvents: "none",
      }} />
      <div style={{ textAlign: "center", maxWidth: 880, position: "relative" }}>
        {children}
      </div>
      {hint && (
        <div style={{
          position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          color: "rgba(255,255,255,0.35)", fontSize: 12, fontWeight: 500,
          letterSpacing: "0.1em", textTransform: "uppercase",
        }}>
          <span>{hint}</span>
          <span className="aiss-bounce" style={{ display: "inline-block" }}>↓</span>
        </div>
      )}
    </section>
  );
}

// ─── the pinned stack ─────────────────────────────────────────────────────────
function StackedSection() {
  const outerRef = useRef(null);
  const [p2, setP2]   = useState(0);
  const [p3, setP3]   = useState(0);
  const [dot, setDot] = useState(0);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const update = () => {
      const rect = outer.getBoundingClientRect();
      const total = outer.offsetHeight - window.innerHeight;
      const raw = clamp(-rect.top / Math.max(1, total), 0, 1);
      // Card 2 progresses through the first ~45% of the pin range,
      // card 3 through the last ~45%, with a brief hold in between.
      setP2(ease(clamp(raw / 0.45, 0, 1)));
      setP3(ease(clamp((raw - 0.5) / 0.45, 0, 1)));
      setDot(raw < 0.22 ? 0 : raw < 0.65 ? 1 : 2);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const transforms = [
    {
      transform: `scale(${(1 - p2 * 0.05).toFixed(4)}) translateY(${(-p2 * 14).toFixed(2)}px)`,
      opacity: +(1 - p2 * 0.4).toFixed(4),
      zIndex: 1,
    },
    {
      transform: `translateY(${((1 - p2) * 104).toFixed(2)}%) scale(${(1 - p3 * 0.05).toFixed(4)}) translateY(${(-p3 * 14).toFixed(2)}px)`,
      opacity: +(1 - p3 * 0.4).toFixed(4),
      zIndex: 2,
    },
    {
      transform: `translateY(${((1 - p3) * 104).toFixed(2)}%)`,
      opacity: 1,
      zIndex: 3,
    },
  ];

  return (
    <section ref={outerRef} className="aiss-section" aria-label="How Premsi AI works">
      {/* pinned viewport */}
      <div className="aiss-pin">
        {/* ambient bg */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(60% 60% at 50% 40%, rgba(139,107,255,0.10), transparent 70%), #050507",
          pointerEvents: "none",
        }} />

        {/* card stage */}
        <div className="aiss-stage">
          {CARDS.map((c, i) => (
            <div key={i} style={{ ...cardBase, ...transforms[i], transition: "none" }}>
              <Card data={c} />
            </div>
          ))}

          <Dots active={dot} accent={CARDS[dot].accent} />
        </div>

        {/* floating section index 
        <div className="aiss-index">
          <span style={{ color: CARDS[dot].accent }}>0{dot + 1}</span>
          <span style={{ width: 24, height: 1, background: "rgba(255,255,255,0.2)" }} />
          <span>Premsi AI · how it works</span>
        </div>*/}
      </div>
    </section>
  );
}

// ─── full section: hero spacer + pinned stack + CTA spacer ────────────────────
export default function AltAIStackedScroll() {
  return (
    <div className="aiss-root">
      {/* spacer above */}
      <Spacer hint="Scroll to see how it works">
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "7px 14px 6px",
          border: "1px solid rgba(139,107,255,0.4)",
          borderRadius: 999, color: "#A89AFF", fontSize: 11.5, fontWeight: 700,
          letterSpacing: "0.18em", textTransform: "uppercase",
        }}>
          <Spark size={11} color="#A89AFF" />
          <span>Premsi for creators</span>
        </div>
        <h1 style={{
          ...syne,
          margin: "32px auto 0",
          fontSize: "clamp(50px, 8vw, 120px)",
          lineHeight: 0.96,
          letterSpacing: "-0.035em",
          fontWeight: 800,
          color: "#fff",
        }}>
          Your AI.<br />
          Your voice.{" "}
          <span style={{
            background: "linear-gradient(180deg, #A89AFF, #6E4DFF)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>Always on.</span>
        </h1>
        {/*<p style={{
          ...inter,
          maxWidth: 640, margin: "30px auto 0",
          fontSize: 19, lineHeight: 1.55, color: "rgba(255,255,255,0.62)",
          textWrap: "pretty",
        }}>
          The chat features below pin to your screen as you scroll. Each one
          slides up to show what Premsi AI does for you and your fans.
        </p>*/}
      </Spacer>

      {/* pinned stack */}
      <StackedSection />

      {/* spacer below */}
      <Spacer>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.4)",
        }}>What's next</div>
        <h2 style={{
          ...syne,
          margin: "20px auto 0",
          fontSize: "clamp(40px, 5.5vw, 80px)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em",
          fontWeight: 800,
          maxWidth: 1000,
          color: "#fff",
        }}>
          Plug your personality in it once.{" "}
          <span style={{
            background: "linear-gradient(180deg, #4DD7A5, #2DB587)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>Let it run.</span>
        </h2>
        <p style={{
          ...inter,
          maxWidth: 620, margin: "26px auto 36px",
          fontSize: 18, lineHeight: 1.55, color: "rgba(255,255,255,0.6)",
        }}>
          Connect your account, train the AI on your voice in five minutes, and
          watch the dashboard light up.
        </p>
        <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <a href="/invitation" style={{
            padding: "14px 22px", borderRadius: 999,
            background: "linear-gradient(180deg, #fff, #e6e6ea)",
            color: "#0a0a0c", fontWeight: 700, textDecoration: "none",
            boxShadow: "0 12px 30px rgba(255,255,255,0.18)",
            fontSize: 15,
          }}>Start creating</a>
          
        </div>
      </Spacer>
    </div>
  );
}
